import type { PostHog } from 'posthog-js';
import { registerErrorClient, filterEvent } from './errorTracking';

const POSTHOG_HOST = 'https://us.i.posthog.com';

let client: PostHog | null = null;

/** Run `cb` when the main thread is idle, with a setTimeout fallback. */
function onIdle(cb: () => void): void {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 1);
  }
}

/**
 * Initialise PostHog: autocapture, SPA pageviews, session replay, and native
 * Core Web Vitals capture (`capture_performance.web_vitals` → `$web_vitals`
 * events that feed PostHog's Web Analytics dashboard with p75/p90 aggregation).
 *
 * Production-only: in dev/test we skip init so the local app stays free of
 * analytics network noise and headless tests don't load the PostHog runtime.
 * Also skipped when `VITE_POSTHOG_KEY` is unset (mirrors the Sentry pattern).
 *
 * The posthog-js bundle is dynamically imported and `init()` runs on idle so
 * the ~50KB+ library and its autocapture/replay instrumentation stay off the
 * critical load path (keeps TBT down). Product events captured before init
 * completes (notably the deeplink `vendor_opened` / first `search_performed`
 * that fire on load) are queued and flushed once init runs, so they aren't
 * lost; the initial pageview is captured by `init` itself.
 */
export function setupAnalytics(): void {
  if (client) return;
  if (!import.meta.env.PROD) return;

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;

  onIdle(() => {
    void import('posthog-js').then(({ default: posthog }) => {
      if (client) return;
      posthog.init(key, {
        api_host: POSTHOG_HOST,
        // Path-based navigation (/list, /listing/…) goes through the History
        // API, so 'history_change' tracks tab/vendor changes as pageviews.
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: true,
        // Native web vitals → $web_vitals events (LCP/CLS/FCP/INP).
        capture_performance: { web_vitals: true },
        // Autocapture unhandled errors + promise rejections (replaces Sentry's
        // global handlers); filterEvent drops known noise.
        capture_exceptions: true,
        before_send: filterEvent,
        // localStorage avoids the consent-cookie question for a low-PII map app.
        persistence: 'localStorage',
        disable_session_recording: false,
        // Mask form inputs (e.g. the vendor search box) in session replay.
        session_recording: { maskAllInputs: true },
      });
      client = posthog;
      for (const { event, properties } of pendingEvents.splice(0)) posthog.capture(event, properties);
      registerErrorClient(posthog);
    });
  });
}

/**
 * Typed product-event map. Keeps capture call sites honest about which
 * properties each event carries. Add new events here, not inline strings.
 */
export interface AnalyticsEvents {
  vendor_opened: {
    vendor_uid: string;
    vendor_name: string;
    category: string;
    vendor_type: string;
    source: 'map' | 'list' | 'deeplink';
  };
  vendor_link_clicked: {
    vendor_uid: string;
    vendor_name: string;
    platform: 'instagram' | 'facebook' | 'website';
  };
  tab_changed: { tab: string; from: string };
  category_filter_changed: {
    category_id: string;
    category_name: string;
    enabled: boolean;
    active_count: number;
  };
  search_performed: { query_length: number; has_results: boolean };
  show_on_map_clicked: { vendor_uid: string; vendor_name: string };
  stage_lineup_viewed: { stage_name: string; has_now_performer: boolean };
}

const pendingEvents: { event: string; properties: Record<string, unknown> }[] = [];
const MAX_PENDING_EVENTS = 20;

/**
 * Capture a typed product event. PostHog loads on idle, so events fired before
 * init (e.g. a deeplink `vendor_opened` on load) are queued (capped) and
 * flushed on init. In dev/test PostHog never initialises, so nothing is ever
 * sent — the queue just fills to its cap and is discarded.
 */
export function captureEvent<E extends keyof AnalyticsEvents>(
  event: E,
  properties: AnalyticsEvents[E],
): void {
  if (client) {
    client.capture(event, properties);
    return;
  }
  if (pendingEvents.length < MAX_PENDING_EVENTS) pendingEvents.push({ event, properties });
}
